import type { APIDesignerStore } from '..';
import type { WritableDraft } from 'immer/dist/internal.js';
import type { OpenAPIV3 } from 'openapi-types';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

import { traverseAndModifySchema, type UpdateSchemaAttrObject } from './utils';

export interface RequestBodyEditorSliceActions {
  /**
   * Adds a new property to the request body schema of the current endpoint.
   *
   * @param id The id of the property to add. This is a string that represents the path to the property in the schema.
   * The first element is always 0, which represents the top-level object.
   * If the id is "0", it means the property should be added to root level of the schema.
   * If the id is "0-1-2", it means the property should be added to the third property of the second property of the root schema object.
   */
  add: (id: string) => void;

  /**
   * Initializes an empty request body for the current endpoint
   */
  init: () => void;

  /**
   * Removes the request body (or a sub-property of the request body) from the current endpoint.
   *
   * @param id The id of the property to remove. This is a string that represents the path to the property in the schema.
   * The first element is always 0, which represents the top-level object.
   * If the id is "0", it means the entire request body should be removed.
   * If the id is "0-1-2", it means the property should be removed from the third property of the second property of the root schema object.
   */
  remove: (id: string) => void;

  /**
   * For a given property, updates a specific attribute (e.g., default value, description, etc.)
   * for this property.
   * 
   * @param id The id of the property to make an attribute update for. This is a string that represents the path to the property in the schema.
   * The first element is always 0, which represents the top-level object.
   * If the id is "0", it means the entire request body should be removed.
   * If the id is "0-1-2", it means the property should be removed from the third property of the second property of the root schema object.

   */
  updateSchemaAttribute: (id: string, updateObject: UpdateSchemaAttrObject) => void;
}

export interface RequestBodyEditorSlice {
  /**
   * State slice containing fields and actions that pertain to the Request Body Editor
   */
  requestBodyEditor: RequestBodyEditorSliceActions;
}

/**
 * Request Body Editor state slice containing fields related to the Manual Editor — Request Body Editor.
 */
export const createRequestBodyEditorSlice: StateCreator<
  APIDesignerStore & RequestBodyEditorSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  RequestBodyEditorSlice
> = set => ({
  requestBodyEditor: {
    add: id => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // if the id is 0, we need to add a new property to the root object
          if (id === '0') {
            if (
              state.apiObject.schema.paths[currentPath][currentMethod].requestBody &&
              'content' in state.apiObject.schema.paths[currentPath][currentMethod].requestBody
            ) {
              state.apiObject.schema.paths[currentPath][currentMethod].requestBody = {
                ...state.apiObject.schema.paths[currentPath][currentMethod].requestBody,
                content: {
                  'application/json': {
                    ...state.apiObject.schema.paths[currentPath][currentMethod].requestBody?.content[
                      'application/json'
                    ],
                    schema: {
                      ...state.apiObject.schema.paths[currentPath][currentMethod].requestBody?.content[
                        'application/json'
                      ]?.schema,
                      properties: {
                        ...(
                          state.apiObject.schema.paths[currentPath][currentMethod].requestBody?.content[
                            'application/json'
                          ]?.schema as WritableDraft<OpenAPIV3.SchemaObject>
                        )?.properties,
                        '': { type: 'string' },
                      },
                    } as WritableDraft<OpenAPIV3.SchemaObject>,
                  },
                },
              };
            } else {
              state.apiObject.schema.paths[currentPath][currentMethod].requestBody = {
                ...state.apiObject.schema.paths[currentPath][currentMethod].requestBody,
                content: {
                  'application/json': {
                    schema: {
                      type: 'object',
                      properties: {
                        '': { type: 'string' },
                      },
                    },
                  },
                },
              };
            }
          } else {
            traverseAndModifySchema(id, state, { action: 'add' });
          }
        },
        false,
        actionLog('requestBodyEditor.add', { id }),
      );
    },

    init: () => {
      const newSchema: OpenAPIV3.NonArraySchemaObject = { properties: {}, type: 'object' };
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          state.apiObject.schema.paths[currentPath][currentMethod].requestBody = {
            content: {
              'application/json': { schema: newSchema },
            },
          };
        },
        false,
        actionLog('requestBodyEditor.init'),
      );
    },

    remove: id => {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          // if the id is 0, we need to remove the entire request body
          if (id === '0') {
            delete state.apiObject.schema.paths[currentPath][currentMethod].requestBody;
          } else {
            traverseAndModifySchema(id, state, { action: 'remove' });
          }
        },
        false,
        actionLog('requestBodyEditor.remove', { id }),
      );
    },

    updateSchemaAttribute(id, updateObject) {
      set(
        state => {
          if (!state.apiObject) throw new Error('apiObject is required');
          if (!state.apiObject.schema) throw new Error('schema is required');
          if (!state.apiObject.schema.paths) throw new Error('paths is required');
          if (!state.apiObject.path) throw new Error('path is required');
          if (!state.apiObject.method) throw new Error('method is required');

          const currentPath = state.apiObject.path;
          const currentMethod = state.apiObject.method;

          if (!state.apiObject.schema.paths[currentPath]?.[currentMethod]) {
            throw new Error(`missing path ${currentPath} for method ${currentMethod}`);
          }

          if (id === '0') {
            // this should never be hit since the root object doesn't have attributes like this (yet!)
          } else {
            traverseAndModifySchema(id, state, { action: 'update', ...updateObject });
          }
        },
        false,
        actionLog('requestBodyEditor.updateSchemaAttribute', { updateObject, id }),
      );
    },
  },
});
